import { useConfig, useHealth } from '../api/hooks';
import { SOURCE_LABELS } from '../api/types';
import type { Mode, SourceHealth } from '../api/types';
import { Role } from '../auth/roles';
import { usePrincipal } from '../auth/usePrincipal';
import { Badge } from '../components/Badge';
import type { Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import { Card, CardHeader, StatCard } from '../components/Card';
import { PageHeader } from '../components/Layout';
import { EmptyState, QueryBoundary } from '../components/states';
import { ModeBadge } from '../components/DomainBadges';
import { useFormatters } from '../time/format';

const STATE_TONES: Record<SourceHealth['state'], Tone> = {
  ok: 'success',
  // Not an error. A source with no secret is skipped and reported, so listing one before its
  // credentials exist is safe — colouring it red would train operators to ignore red.
  unconfigured: 'neutral',
  failed: 'danger',
};

const SECRET_TONES: Record<string, Tone> = {
  set: 'success',
  placeholder: 'danger',
  unknown: 'warning',
};

export function Health() {
  const principal = usePrincipal();
  const admin = principal.role >= Role.ADMIN;
  const health = useHealth(admin);
  const config = useConfig(admin);
  const { precise, relative } = useFormatters();

  if (!admin) {
    return (
      <>
        <PageHeader title="Health" />
        <Card>
          <EmptyState
            title="Health requires the IRA.Admin role"
            detail="This page exposes source cursors and secret status, so it is admin-only."
          />
        </Card>
      </>
    );
  }

  const placeholders = Object.entries(health.data?.secrets ?? {}).flatMap(([secret, keys]) =>
    Object.entries(keys)
      .filter(([, state]) => state === 'placeholder')
      .map(([key]) => `${secret}/${key}`),
  );

  const autoModes = Object.entries(config.data?.action_modes ?? {}).filter(
    ([, mode]) => mode === 'auto',
  );

  return (
    <>
      <PageHeader
        title="Health"
        subtitle={`Source cursors, secret status and the deployed policy. ${health.data?.version ? `Version ${health.data.version}.` : ''}`}
      />

      <QueryBoundary
        isLoading={health.isPending}
        error={health.error}
        onRetry={() => void health.refetch()}
        loadingLabel="Loading health"
      >
        <div className="space-y-6">
          {/*
            A secret still holding its CDK placeholder is a deployment that looks complete and
            is not: the source will fail on its first poll. Called out at the top because it
            is the single most common cause of a silent gap in intake.
          */}
          {placeholders.length > 0 ? (
            <Callout tone="danger" role="alert">
              <p className="font-medium">
                {placeholders.length} secret {placeholders.length === 1 ? 'key' : 'keys'} still
                hold a placeholder
              </p>
              <p className="mt-0.5 opacity-90">
                These were created by the deploy and never populated. The sources depending on
                them cannot authenticate.
              </p>
              <ul className="mt-1 font-mono text-[11px]">
                {placeholders.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </Callout>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              label="Sources reporting ok"
              value={`${health.data?.sources.filter((s) => s.state === 'ok').length ?? 0} / ${health.data?.sources.length ?? 0}`}
            />
            <StatCard
              label="Automatic actions this hour"
              value={health.data?.auto_actions_last_hour ?? 0}
              hint={
                config.data
                  ? `Cap is ${config.data.auto_actions_per_hour} platform-wide`
                  : undefined
              }
              tone={
                config.data &&
                (health.data?.auto_actions_last_hour ?? 0) >= config.data.auto_actions_per_hour
                  ? 'warning'
                  : undefined
              }
            />
            <StatCard
              label="Actions set to auto"
              value={autoModes.length}
              hint={
                autoModes.length === 0
                  ? 'Everything requires approval or is disabled'
                  : autoModes.map(([name]) => name).join(', ')
              }
              tone={autoModes.length > 0 ? 'warning' : undefined}
            />
          </div>

          <Card className="p-0">
            <div className="p-5 pb-0">
              <CardHeader title="Sources" subtitle="Intake state per feed" />
            </div>
            <div className="overflow-x-auto scroll-thin">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-line text-xs uppercase tracking-wide text-subtle">
                  <tr>
                    <th scope="col" className="px-5 py-3 font-semibold">
                      Source
                    </th>
                    <th scope="col" className="px-3 py-3 font-semibold">
                      State
                    </th>
                    <th scope="col" className="px-3 py-3 font-semibold">
                      Last success
                    </th>
                    <th scope="col" className="px-5 py-3 font-semibold">
                      Last error
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {(health.data?.sources ?? []).map((source) => (
                    <tr key={source.source}>
                      <td className="px-5 py-3">
                        <span className="font-medium text-body">
                          {SOURCE_LABELS[source.source] ?? source.source}
                        </span>
                        {!source.enabled ? (
                          <span className="ml-2 text-xs text-subtle">(not enabled)</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3">
                        <Badge tone={STATE_TONES[source.state]}>{source.state}</Badge>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-muted">
                        {source.last_success ? (
                          <span title={precise(source.last_success)}>
                            {relative(source.last_success)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-danger-fg">
                        {source.last_error || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Secrets"
              subtitle="Whether each key has been populated. Values are never returned."
            />
            {Object.keys(health.data?.secrets ?? {}).length === 0 ? (
              <EmptyState title="No secrets reported" />
            ) : (
              <div className="space-y-3">
                {Object.entries(health.data?.secrets ?? {}).map(([secret, keys]) => (
                  <div key={secret}>
                    <p className="mb-1 font-mono text-xs text-muted">{secret}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(keys).map(([key, state]) => (
                        <Badge key={key} tone={SECRET_TONES[state] ?? 'neutral'}>
                          {key}: {state}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Containment policy"
              subtitle="From cdk.json. Changing it is a deliberate config change and a redeploy."
            />
            <QueryBoundary
              isLoading={config.isPending}
              error={config.error}
              onRetry={() => void config.refetch()}
            >
              {config.data ? (
                <>
                  <div className="mb-4 flex flex-wrap gap-2">
                    {Object.entries(config.data.action_modes)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([action, mode]) => (
                        <span
                          key={action}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-sunken px-2 py-1 text-xs"
                        >
                          <span className="font-mono text-muted">{action}</span>
                          <ModeBadge mode={mode as Mode} />
                        </span>
                      ))}
                  </div>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                    <PolicyRow
                      label="Automatic actions per hour"
                      value={String(config.data.auto_actions_per_hour)}
                    />
                    <PolicyRow
                      label="Approval TTL"
                      value={`${config.data.approval_ttl_hours} hours`}
                    />
                    <PolicyRow
                      label="Complete evidence required for auto"
                      value={config.data.require_complete_evidence_for_auto ? 'yes' : 'no'}
                    />
                    <PolicyRow
                      label="Evidence token budget"
                      value={config.data.evidence_token_budget.toLocaleString()}
                    />
                    <PolicyRow
                      label="Protected principals"
                      value={
                        config.data.protected_principals.length === 0
                          ? 'none configured'
                          : config.data.protected_principals.join(', ')
                      }
                    />
                  </dl>
                  {config.data.protected_principals.length === 0 && autoModes.length > 0 ? (
                    <div className="mt-3">
                      <Callout tone="warning">
                        An action is set to <code className="font-mono">auto</code> with no
                        protected principals configured. Break-glass and directory-admin
                        accounts have no exclusion.
                      </Callout>
                    </div>
                  ) : null}
                </>
              ) : null}
            </QueryBoundary>
          </Card>
        </div>
      </QueryBoundary>
    </>
  );
}

function PolicyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line pb-1">
      <dt className="text-subtle">{label}</dt>
      <dd className="text-right font-medium text-body">{value}</dd>
    </div>
  );
}
